import React, { createContext, useState, useContext, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = React.useCallback((silent = false) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    if (!silent) {
      toast.info('Logged out successfully');
    }
  }, []);

  // Initialize auth state from localStorage or sessionStorage (for password manager flow)
  useEffect(() => {
    // Check sessionStorage first (used for password manager detection flow)
    const sessionToken = sessionStorage.getItem('authToken');
    const sessionUser = sessionStorage.getItem('user');
    
    if (sessionToken && sessionUser) {
      // Move from sessionStorage to localStorage
      localStorage.setItem('token', sessionToken);
      localStorage.setItem('user', sessionUser);
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
    }

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;

        // Check if token is expired
        if (decodedToken.exp < currentTime) {
          logout(true);
        } else {
          setUser(JSON.parse(userData));
        }
      } catch (error) {
        console.error('Error decoding token:', error);
        logout(true);
      }
    }

    setLoading(false);
  }, [logout]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    if (userData?.subscriptionPlan !== 'pro') {
      toast.success('Login successful!');
    }
  };

  const updateUser = (userData) => {
    const token = localStorage.getItem('token');
    if (token) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (role) => {
    return user && user.role === role;
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
